// Yüklemeye hazırlanan görsellerin boyut / biçim mantığı.
//
// SORUN (kökten): panelden fotoğraf yüklemek üç ayrı yerde tıkanıyordu.
//
//   1. BOYUT — Sunucudaki yükleme uçları bir boyut sınırı uyguluyor. Telefon
//      fotoğrafları (12 MP, 4-8 MB) bu sınırı aşıyor. Eski sürüm yalnızca
//      sınırı AŞAN dosyaları küçültüyordu; sınırın hemen altındaki 4 MB'lık bir
//      fotoğraf tam çözünürlükte yükleniyor, mobil bağlantıda dakikalarca
//      sürüyor ve isteğin zaman aşımına düşmesine yol açıyordu.
//
//   2. YÖN (EXIF) — Telefon fotoğrafları dik çekilse bile baytları yatay
//      tutulur; doğru yön EXIF etiketindedir. Görsel canvas'a `<img>` üzerinden
//      çizildiğinde bu etiket kaybolur ve fotoğraf yan yatmış olarak yüklenirdi.
//
//   3. HEIC — iPhone varsayılan biçimi HEIC/HEIF. Tarayıcıların çoğu bu biçimi
//      `<img>` ile çözemez; eski sürüm çözemediğinde dosyayı OLDUĞU GİBİ
//      gönderiyordu. Sunucu kabul etse bile tarayıcı HEIC'i çizemediği için
//      görsel her yerde kırık görünüyordu. Artık önce `createImageBitmap` ile
//      çözülmeye çalışılır (Safari HEIC'i böyle açar), olmazsa kullanıcıya ne
//      yapması gerektiğini söyleyen bir hata verilir.
//
// ÇÖZÜM: her görsel yüklemeden önce normalize edilir —
//   - Uzun kenar MAX_UPLOAD_EDGE'e indirilir (yalnızca küçültme).
//   - Şeffaflık yoksa JPEG'e geçilir (fotoğrafta ~10 kat küçük dosya).
//   - Şeffaflık varsa PNG korunur (logo/kesilmiş arka plan bozulmasın diye).
//   - Zaten küçük ve web'de görüntülenebilir dosyalar olduğu gibi bırakılır.
//
// Bu modüldeki karar yardımcıları (canvas'a dokunmayan kısımlar) saf tutulmuştur;
// birim testleri bunları doğrular.

/** Sunucudaki görsel yükleme uçlarının sınırı (middleware/imageUpload.js). */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/**
 * Hedef dosya boyutu.
 *
 * Sunucu sınırı emniyet kemeri; asıl hedef isteğin makul sürede tamamlanması.
 * 3 MB, sahadaki mobil bağlantıda birkaç saniyede giden ve baskı kalitesini
 * koruyan bir denge.
 */
export const UPLOAD_BUDGET_BYTES = 3 * 1024 * 1024;

/** Yüklenecek görselin en uzun kenarı. */
export const MAX_UPLOAD_EDGE = 1600;

/** JPEG kalite merdiveni: sığana kadar sırayla denenir. */
export const JPEG_QUALITY_STEPS = [0.92, 0.85, 0.75, 0.65];

/** Kalite düşürmek yetmezse uygulanan ölçek merdiveni. */
export const SCALE_STEPS = [1, 0.8, 0.65, 0.5, 0.4];

/**
 * Tarayıcının `<img>` ile güvenle çizebildiği biçimler.
 *
 * Bu listede olmayan bir görsel (HEIC, HEIF, TIFF, BMP) yüklenmeden önce
 * mutlaka dönüştürülür; aksi hâlde depoya konur ama hiçbir yerde görünmez.
 */
export const WEB_SAFE_TYPES = Object.freeze([
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const IMAGE_TYPE = /^image\//i;

/** Uzantıdan da anlaşılabilen, tarayıcının çözemediği biçimler. */
const NON_WEB_EXT = /\.(heic|heif|tif|tiff|bmp|avif)$/i;

const typeOf = (file) => String(file?.type || "").toLowerCase();
const nameOf = (file) => String(file?.name || "");

/**
 * Dosya, tarayıcının doğrudan gösterebileceği bir biçimde mi?
 *
 * Tür boş gelebilir (bazı Android dosya seçicileri MIME vermez); o durumda
 * uzantıya bakılır.
 *
 * @param {{ type?: string, name?: string }|null} file
 * @returns {boolean}
 */
export const isWebSafeImage = (file) => {
  if (!file) return false;
  const type = typeOf(file);
  if (type) return WEB_SAFE_TYPES.includes(type);
  return Boolean(nameOf(file)) && !NON_WEB_EXT.test(nameOf(file));
};

/**
 * Dosya HEIC/HEIF mi? (iPhone varsayılan biçimi)
 *
 * @param {{ type?: string, name?: string }|null} file
 * @returns {boolean}
 */
export const isHeicImage = (file) => {
  if (!file) return false;
  const type = typeOf(file);
  if (type === "image/heic" || type === "image/heif") return true;
  return /\.(heic|heif)$/i.test(nameOf(file));
};

/**
 * Bu dosya yükleme öncesi işlenmeli mi?
 *
 * Görsel olmayan dosyalara dokunulmaz. Bütçeyi aşan VEYA tarayıcının
 * çizemediği biçimdeki dosyalar (HEIC, TIFF, BMP) mutlaka işlenir. Bütçeye
 * sığan web uyumlu bir dosya olduğu gibi bırakılır: yeniden kodlamak gereksiz
 * kalite kaybıdır ve logolarda fark edilir.
 *
 * @param {{ type?: string, size?: number, name?: string }|null} file
 * @param {number} [budget]
 * @returns {boolean}
 */
export const needsRecompression = (file, budget = UPLOAD_BUDGET_BYTES) => {
  if (!file) return false;
  const type = typeOf(file);
  // Tür boşsa uzantıya güven: bazı dosya seçiciler MIME göndermez.
  if (type && !IMAGE_TYPE.test(type)) return false;
  if (!type && !/\.[a-z0-9]+$/i.test(nameOf(file))) return false;
  if (!isWebSafeImage(file)) return true;
  return Number(file.size || 0) > budget;
};

/**
 * Çıktı biçimi: şeffaflık korunmalıysa PNG, aksi halde JPEG.
 *
 * Şeffaf bir görseli JPEG'e çevirmek zemini siyaha boyar; logo ve arka planı
 * temizlenmiş fotoğraflar bu yüzden PNG kalır.
 *
 * @param {{ hasAlpha?: boolean }} [options]
 * @returns {{ mimeType: string, extension: string }}
 */
export const pickUploadFormat = ({ hasAlpha = false } = {}) =>
  hasAlpha
    ? { mimeType: "image/png", extension: ".png" }
    : { mimeType: "image/jpeg", extension: ".jpg" };

/**
 * Dosya adını çıktı biçimine göre yeniden yazar.
 *
 * Sunucudaki süzgeç hem MIME türünü hem UZANTIYI beyaz listeye karşı
 * denetliyor; ayrıca depodaki nesne adının içeriğiyle uyuşması için uzantı
 * biçime göre yazılır.
 *
 * @param {string} name
 * @param {string} extension
 * @returns {string}
 */
export const withExtension = (name, extension) => {
  const base = String(name || "").replace(/\.[^./\\]+$/, "").trim();
  return `${base || "gorsel"}${extension}`;
};

/**
 * Uzun kenarı sınırlayan ölçek çarpanı (büyütme yapmaz).
 *
 * @param {number} width
 * @param {number} height
 * @param {number} [maxEdge]
 * @returns {number}
 */
export const fitScale = (width, height, maxEdge = MAX_UPLOAD_EDGE) => {
  const longest = Math.max(Number(width) || 0, Number(height) || 0);
  if (!longest || longest <= maxEdge) return 1;
  return maxEdge / longest;
};

/**
 * Denenecek (ölçek, kalite) adımları: önce kaliteyi düşür, yetmezse küçült.
 *
 * PNG'de kalite parametresi yoktur; yalnızca ölçek merdiveni kullanılır.
 *
 * @param {string} mimeType
 * @returns {Array<{ scale: number, quality: number|undefined }>}
 */
export const encodingAttempts = (mimeType) => {
  if (mimeType === "image/png") return SCALE_STEPS.map((scale) => ({ scale, quality: undefined }));
  return SCALE_STEPS.flatMap((scale) =>
    JPEG_QUALITY_STEPS.map((quality) => ({ scale, quality }))
  );
};

/**
 * Görsel hazırlanamadığında atılan, kullanıcıya gösterilebilir hata.
 *
 * Sessizce özgün dosyayı göndermek yerine hata atılır: HEIC'te "yüklendi ama
 * görünmüyor" sonucunu doğuran davranış buydu.
 */
export class ImagePrepareError extends Error {
  constructor(message, code = "IMAGE_PREPARE_FAILED") {
    super(message);
    this.name = "ImagePrepareError";
    this.code = code;
  }
}

export const HEIC_MESSAGE =
  "Bu fotoğraf iPhone'un HEIC biçiminde ve tarayıcı bu biçimi açamıyor. " +
  "iPhone'da Ayarlar → Kamera → Biçimler → \"En Uyumlu\" seçeneğini işaretleyip " +
  "fotoğrafı yeniden çekin ya da fotoğrafı JPEG olarak kaydedip tekrar deneyin.";

/* -------------------------------------------------------------------------- */
/* Canvas'a dokunan kısım                                                      */
/* -------------------------------------------------------------------------- */

const canUseBitmap = () => typeof createImageBitmap === "function";

/**
 * Dosyayı çizilebilir bir kaynağa çevirir.
 *
 * Önce `createImageBitmap` denenir:
 *   - EXIF yönünü uygular (`imageOrientation: "from-image"`), böylece telefonla
 *     dik çekilmiş fotoğraf yan yatmaz;
 *   - dosyayı data-URL'e çevirmeden çözer (10 MB'lık bir fotoğrafın base64
 *     karşılığı ~13 MB metindir; mobil Safari bunda bellek yetmezliğine düşüyor);
 *   - Safari'de HEIC'i de açabilir.
 *
 * Olmazsa object URL üzerinden `<img>` ile çözülür (data-URL'e göre yine çok
 * daha ucuz).
 *
 * @param {File|Blob} file
 * @returns {Promise<{ source: CanvasImageSource, width: number, height: number, release: () => void }>}
 */
const decodeImage = async (file) => {
  if (canUseBitmap()) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.(),
      };
    } catch {
      // Tarayıcı seçeneği desteklemiyor olabilir: seçeneksiz bir kez daha dene.
      try {
        const bitmap = await createImageBitmap(file);
        return {
          source: bitmap,
          width: bitmap.width,
          height: bitmap.height,
          release: () => bitmap.close?.(),
        };
      } catch {
        /* `<img>` yoluna düş */
      }
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Görsel çözümlenemedi."));
      element.src = url;
    });
    return {
      source: image,
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Görsel kodlanamadı."))),
      mimeType,
      quality
    );
  });

/**
 * Görselde şeffaf piksel var mı?
 *
 * Örnekleme yapılmaz: logolarda şeffaflık çoğu zaman ince bir kenar payıdır ve
 * atlanırsa görsel JPEG'e çevrilip zemini kararır. Tam tarama birkaç ms sürer.
 */
const detectAlpha = (context, width, height) => {
  try {
    const { data } = context.getImageData(0, 0, width, height);
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] < 250) return true;
    }
    return false;
  } catch {
    // Canvas okunamıyorsa (tainted) güvenli taraf: şeffaflık varsay, PNG kal.
    return true;
  }
};

const drawScaled = (source, sourceWidth, sourceHeight, scale) => {
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(source, 0, 0, width, height);
  return { canvas, context, width, height };
};

/**
 * Bir canvas'ı yükleme bütçesine sığan dosyaya çevirir.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {string} fileName Uzantısız dosya adı
 * @param {{ budget?: number, hasAlpha?: boolean }} [options]
 * @returns {Promise<File>}
 */
export const canvasToUploadFile = async (canvas, fileName, { budget = UPLOAD_BUDGET_BYTES, hasAlpha } = {}) => {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const alpha = hasAlpha === undefined ? detectAlpha(context, canvas.width, canvas.height) : hasAlpha;
  const { mimeType, extension } = pickUploadFormat({ hasAlpha: alpha });
  const name = withExtension(fileName, extension);

  // Ölçek düşürmek gerekmezse kaynak görseli hiç üretmeyiz; canvas'ı yeniden
  // çözmek pahalı bir adımdır.
  let sourcePromise = null;
  const scaledCanvas = async (scale) => {
    if (!sourcePromise) {
      sourcePromise = canvasToBlob(canvas, "image/png").then((blob) => decodeImage(blob));
    }
    const decoded = await sourcePromise;
    return drawScaled(decoded.source, decoded.width, decoded.height, scale).canvas;
  };

  let smallest = null;
  try {
    for (const attempt of encodingAttempts(mimeType)) {
      const target = attempt.scale === 1 ? canvas : await scaledCanvas(attempt.scale);
      const blob = await canvasToBlob(target, mimeType, attempt.quality);
      if (!smallest || blob.size < smallest.size) smallest = blob;
      if (blob.size <= budget) return new File([blob], name, { type: mimeType });
    }
  } finally {
    if (sourcePromise) {
      await sourcePromise.then((decoded) => decoded.release?.()).catch(() => {});
    }
  }

  // Hiçbir adım sığmadıysa en küçüğü döndür; sunucu mesajı kullanıcıya ulaşır.
  return new File([smallest], name, { type: mimeType });
};

/**
 * Seçilen dosyayı yüklemeye hazırlar: yönünü düzeltir, gerekiyorsa küçültür ve
 * web'de görüntülenebilir bir biçime kodlar.
 *
 * Hiç dokunulması gerekmeyen dosyalar (bütçeye sığan, web uyumlu, makul
 * çözünürlükte) olduğu gibi döner.
 *
 * @param {File} file
 * @param {{ budget?: number, maxEdge?: number }} [options]
 * @returns {Promise<File>}
 * @throws {ImagePrepareError} Görsel çözülemediğinde (ör. masaüstünde HEIC).
 */
export const prepareImageForUpload = async (file, { budget = UPLOAD_BUDGET_BYTES, maxEdge = MAX_UPLOAD_EDGE } = {}) => {
  if (!needsRecompression(file, budget)) return file;

  let decoded = null;
  try {
    decoded = await decodeImage(file);
  } catch {
    // Çözülemeyen dosyayı sessizce göndermek, depoda görüntülenemeyen bir
    // nesne bırakır. Kullanıcıya ne yapması gerektiği söylenir.
    if (isHeicImage(file)) throw new ImagePrepareError(HEIC_MESSAGE, "HEIC_UNSUPPORTED");
    throw new ImagePrepareError(
      "Bu görsel açılamadı. Dosya bozuk olabilir; JPEG ya da PNG olarak kaydedip tekrar deneyin.",
      "IMAGE_DECODE_FAILED"
    );
  }

  try {
    const scale = fitScale(decoded.width, decoded.height, maxEdge);
    const { canvas, context, width, height } = drawScaled(decoded.source, decoded.width, decoded.height, scale);
    const hasAlpha = detectAlpha(context, width, height);
    return await canvasToUploadFile(canvas, file.name, { budget, hasAlpha });
  } catch {
    // Kodlama başarısız olduysa özgün dosya hâlâ web uyumluysa denenebilir.
    if (isWebSafeImage(file)) return file;
    throw new ImagePrepareError(
      "Görsel yüklemeye hazırlanamadı. JPEG ya da PNG olarak kaydedip tekrar deneyin.",
      "IMAGE_ENCODE_FAILED"
    );
  } finally {
    decoded.release?.();
  }
};

export default prepareImageForUpload;
